import { useState, useEffect, useCallback } from "react";
import {
  Plus, X, Loader2, Trash2, Edit3, FileSpreadsheet, Download, Upload,
  Search, Cable, Zap, Server, ToggleRight, Package, CheckSquare, Square
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { fmt, opts } from "./constants";
import { useCatalogoPresupuesto, CatalogoScopeBanner, CatalogoSinGuardar } from "./catalogoScope";

// Sub-pestañas (categorías)
const CATEGORIAS = [
  { key: "cables",            label: "Cables",            icon: Cable,       color: "text-orange-600" },
  { key: "disyuntores",       label: "Disyuntores",       icon: Zap,         color: "text-yellow-600" },
  { key: "tableros",          label: "Tableros",          icon: Server,      color: "text-blue-600" },
  { key: "mecanismos",        label: "Mecanismos",        icon: ToggleRight, color: "text-violet-600" },
  { key: "materiales_varios", label: "Materiales Varios", icon: Package,     color: "text-gray-600" },
];

// Definición de columnas mostradas por categoría
const COLUMNAS = {
  cables: [
    { key: "marca",       label: "Marca", className: "font-semibold text-orange-700" },
    { key: "calibre",     label: "Calibre", meta: true },
    { key: "tipo",        label: "Tipo", meta: true },
    { key: "descripcion", label: "Descripción" },
  ],
  disyuntores: [
    { key: "marca",       label: "Marca", className: "font-semibold text-yellow-700" },
    { key: "modelo",      label: "Modelo" },
    { key: "polos",       label: "Polos", meta: true, align: "center" },
    { key: "amperaje",    label: "Amperaje", meta: true, suffix: "A", align: "center" },
    { key: "descripcion", label: "Descripción" },
  ],
  tableros: [
    { key: "marca",       label: "Marca", className: "font-semibold text-blue-700" },
    { key: "modelo",      label: "Modelo" },
    { key: "tipo",        label: "Tipo", meta: true },
    { key: "descripcion", label: "Descripción" },
  ],
  mecanismos: [
    { key: "marca",       label: "Marca", className: "font-semibold text-violet-700" },
    { key: "modelo",      label: "Modelo" },
    { key: "tipo",        label: "Tipo", meta: true },
    { key: "descripcion", label: "Descripción" },
  ],
  materiales_varios: [
    { key: "modelo",      label: "Código", className: "font-semibold text-gray-700" },
    { key: "descripcion", label: "Descripción" },
  ],
};

// Campos del modal por categoría
const CAMPOS_MODAL = {
  cables: [
    { key: "marca",       label: "Marca",       placeholder: "NEXANS / CORDEIRO / MEGACABOS" },
    { key: "calibre",     label: "Calibre",     placeholder: "1x2.5 / 1x95N / 1x50T", meta: true },
    { key: "tipo",        label: "Tipo",        placeholder: "Multifilar / Neutro / Tierra", meta: true },
    { key: "descripcion", label: "Descripción", placeholder: "Cable multifilar 2.5mm²" },
  ],
  disyuntores: [
    { key: "marca",       label: "Marca",       placeholder: "SCHNEIDER / ABB / CHINT / HYUNDAI" },
    { key: "modelo",      label: "Modelo",      placeholder: "iC60N / S203 / DZ47" },
    { key: "polos",       label: "Polos",       placeholder: "1, 2, 3, 4", meta: true, type: "number" },
    { key: "amperaje",    label: "Amperaje (A)", placeholder: "16, 32, 40", meta: true, type: "number" },
    { key: "descripcion", label: "Descripción", placeholder: "Disyuntor 2P 16A" },
  ],
  tableros: [
    { key: "marca",       label: "Marca",       placeholder: "COMINSA / ARGO / BRUM" },
    { key: "modelo",      label: "Modelo",      placeholder: "TGA-12 / PB-300" },
    { key: "tipo",        label: "Tipo",        placeholder: "Empotrado / Sobrepuesto / Pedestal", meta: true },
    { key: "descripcion", label: "Descripción", placeholder: "Tablero general 12 módulos" },
  ],
  mecanismos: [
    { key: "marca",       label: "Marca",       placeholder: "VIMAR NEVE / CONATEL / CAMBRE" },
    { key: "modelo",      label: "Modelo",      placeholder: "1L / TUG / 2L" },
    { key: "tipo",        label: "Tipo",        placeholder: "Mecanismo / Tomacorriente", meta: true },
    { key: "descripcion", label: "Descripción", placeholder: "Llave de luz simple" },
  ],
  materiales_varios: [
    { key: "modelo",      label: "Código",      placeholder: "TUBO 3/4 / CAJA-PL" },
    { key: "descripcion", label: "Descripción", placeholder: "Caño conduit corrugado" },
  ],
};

const cellValue = (item, col) => {
  if (col.meta) return item.meta?.[col.key] ?? "—";
  return item[col.key] ?? "—";
};

export function CatalogosExtTab() {
  const [activeCat, setActiveCat] = useState("cables");
  // Fix CC (2026-09-10): dentro de un presupuesto, su copia; en la Base Madre, la base.
  const { budgetId, sinGuardar } = useCatalogoPresupuesto();
  const [version, setVersion] = useState(0); // al traer la base madre se recarga la sección

  if (sinGuardar) return <CatalogoSinGuardar que="Los catálogos" />;

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
      {budgetId && <CatalogoScopeBanner onRecopiado={() => setVersion(v => v + 1)} />}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="mb-5">
          <h3 className="text-sm font-bold text-[#1A4B7A] uppercase tracking-wide flex items-center gap-2">
            <FileSpreadsheet size={14} /> Catálogos de Precios
          </h3>
          <p className="text-xs text-gray-500 mt-1">Catálogos de Cables, Disyuntores, Tableros, Mecanismos y Materiales Varios — usados como sugerencia en C.Unit MAT del presupuesto.</p>
        </div>

        {/* Sub-pestañas */}
        <div className="flex gap-1.5 mb-5 flex-wrap" data-testid="catalogos-subtabs">
          {CATEGORIAS.map(cat => {
            const Icon = cat.icon;
            const isActive = activeCat === cat.key;
            return (
              <button key={cat.key} onClick={() => setActiveCat(cat.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  isActive
                    ? "bg-[#2E6DA4] text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`catalogo-tab-${cat.key}`}
              >
                <Icon size={13} className={isActive ? "" : cat.color} /> {cat.label}
              </button>
            );
          })}
        </div>

        {/* Tabla del catálogo activo */}
        <CatalogoSection key={`${activeCat}-${version}`} categoria={activeCat} />
      </div>
    </div>
  );
}

// ─── Sección genérica para una categoría ──────────────────────────────────
function CatalogoSection({ categoria }) {
  const { catApi, budgetId } = useCatalogoPresupuesto(); // Fix CC (2026-09-10)
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMarca, setFilterMarca] = useState("todas");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  // Fix AF (2026-09-04): selección múltiple (mismo patrón que BocasTab).
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${catApi}/catalogos/${categoria}`, opts);
      setItems(Array.isArray(data) ? data : []);
    } catch { toast.error(`Error al cargar ${categoria}`); }
    finally { setLoading(false); }
  }, [categoria, catApi]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Marcas únicas para filtro
  const marcasUnicas = [...new Set(items.map(i => (i.marca || "").trim()).filter(Boolean))].sort();

  // Fix AF (2026-09-04, testing_agent): si la marca filtrada ya no existe (ej.
  // se borraron en bloque todos los ítems de esa marca), volver a "todas" para
  // no dejar el filtro "huérfano" con la tabla vacía sin control visible.
  useEffect(() => {
    if (filterMarca !== "todas" && !marcasUnicas.includes(filterMarca)) {
      setFilterMarca("todas");
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = items.filter(it => {
    const matchSearch = !search ||
      (it.descripcion || "").toLowerCase().includes(search.toLowerCase()) ||
      (it.modelo || "").toLowerCase().includes(search.toLowerCase()) ||
      (it.marca || "").toLowerCase().includes(search.toLowerCase());
    const matchMarca = filterMarca === "todas" || (it.marca || "").trim() === filterMarca;
    return matchSearch && matchMarca;
  });

  // Fix AF (2026-09-04): "seleccion total o seleccion de varios items". "Todos"
  // selecciona los VISIBLES (los que pasan buscador + filtro de marca), así se
  // puede filtrar una marca y borrar solo esa.
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const visibleIds = filtered.map((it) => it._id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`¿Eliminar ${selected.size} ítem${selected.size !== 1 ? "s" : ""} del catálogo de ${categoria}?\nEsta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${catApi}/catalogos/${categoria}/bulk-delete`, { ids: Array.from(selected) }, opts);
      toast.success(`${data.deleted || 0} ítem${(data.deleted || 0) !== 1 ? "s" : ""} eliminado${(data.deleted || 0) !== 1 ? "s" : ""}`);
      clearSelection();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar ítems");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(`¿Eliminar este ítem del catálogo de ${categoria}?`)) return;
    try {
      await axios.delete(`${catApi}/catalogos/${categoria}/${id}`, opts);
      toast.success("Ítem eliminado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/catalogos/${categoria}/template`, "_blank");
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!confirm(`¿Importar "${file.name}" al catálogo de ${categoria}?`)) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/catalogos/${categoria}/import-excel?replace=false`, fd, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} ítems importados (${data.skipped} omitidos)`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar Excel");
    } finally { setImporting(false); }
  };

  const cols = COLUMNAS[categoria];

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-start gap-2 flex-wrap mb-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar marca / modelo / descripción..."
            className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2E6DA4]/30"
            data-testid={`cat-search-${categoria}`} />
        </div>
        {marcasUnicas.length > 1 && (
          <select value={filterMarca} onChange={e => setFilterMarca(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-[#2E6DA4]/30"
            data-testid={`cat-marca-filter-${categoria}`}>
            <option value="todas">Todas las marcas</option>
            {marcasUnicas.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* Fix CC (2026-09-10): importar planillas escribe la BASE MADRE: dentro de un presupuesto no se muestra. */}
          {!budgetId && (<>
          <button onClick={handleDownloadTemplate}
            className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
            data-testid={`cat-template-${categoria}`}>
            <Download size={13} /> Plantilla Excel
          </button>
          <label className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer">
            {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {importing ? "Importando..." : "Importar Excel"}
            <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" disabled={importing} data-testid={`cat-import-${categoria}`} />
          </label>
          </>)}
          <button onClick={() => setEditing("new")}
            className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
            data-testid={`cat-new-${categoria}`}>
            <Plus size={13} /> Nuevo
          </button>
        </div>
      </div>

      {/* Fix AF (2026-09-04) — Barra de acciones masivas (visible cuando hay selección) */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2 bg-red-50 border-2 border-red-200 rounded-xl" data-testid={`cat-bulk-action-bar-${categoria}`}>
          <CheckSquare size={14} className="text-red-600 flex-shrink-0" />
          <span className="text-xs font-bold text-red-700">
            {selected.size} ítem{selected.size !== 1 ? "s" : ""} seleccionado{selected.size !== 1 ? "s" : ""}
          </span>
          <button onClick={clearSelection} className="text-xs font-medium text-red-700 hover:underline">
            Limpiar selección
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            className="ml-auto flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-60"
            data-testid={`cat-bulk-delete-btn-${categoria}`}
          >
            {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {bulkDeleting ? "Eliminando..." : `Eliminar ${selected.size} ítem${selected.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-xs">
          <thead className="bg-[#EBF3FB] border-b border-blue-100">
            <tr>
              <th className="w-10 px-3 py-2.5 text-center">
                <button
                  onClick={toggleAll}
                  className="text-[#1A4B7A] hover:text-[#2E6DA4] inline-flex items-center justify-center"
                  title={allSelected ? "Deseleccionar todos" : "Seleccionar todos los visibles"}
                  data-testid={`cat-select-all-${categoria}`}
                  aria-label="Seleccionar todos"
                >
                  {allSelected ? <CheckSquare size={15} /> : someSelected ? <CheckSquare size={15} className="opacity-60" /> : <Square size={15} />}
                </button>
              </th>
              {cols.map(c => (
                <th key={c.key} className={`px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide ${c.align === "center" ? "text-center" : "text-left"}`}>
                  {c.label}
                </th>
              ))}
              <th className="text-right px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Precio Unit.</th>
              <th className="text-center px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Un.</th>
              <th className="text-right px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide w-24">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-50">
                  {Array.from({ length: cols.length + 4 }).map((__, j) => <td key={j} className="px-3 py-2.5"><Skeleton className="h-4 w-full" /></td>)}
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={cols.length + 4} className="text-center py-12 text-gray-400">
                  <Package size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="font-medium">No hay ítems en el catálogo</p>
                  <p className="text-xs mt-1">Importá una planilla Excel o creá manualmente.</p>
                </td>
              </tr>
            ) : (
              filtered.map(it => (
                <tr key={it._id} className={`border-b border-gray-50 hover:bg-gray-50/60 ${selected.has(it._id) ? "bg-blue-50/40" : ""}`} data-testid={`cat-${categoria}-row-${it._id}`}>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={() => toggleOne(it._id)}
                      className="text-[#1A4B7A] hover:text-[#2E6DA4] inline-flex items-center justify-center"
                      data-testid={`cat-select-${it._id}`}
                      aria-label="Seleccionar"
                    >
                      {selected.has(it._id) ? <CheckSquare size={14} /> : <Square size={14} />}
                    </button>
                  </td>
                  {cols.map(c => (
                    <td key={c.key} className={`px-3 py-2.5 ${c.align === "center" ? "text-center" : ""} ${c.className || "text-gray-800"}`}>
                      {cellValue(it, c)}{c.suffix && cellValue(it, c) !== "—" ? c.suffix : ""}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums text-blue-700">{fmt(it.precio_unitario)}</td>
                  <td className="px-3 py-2.5 text-center text-gray-600">{it.unidad}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => setEditing(it)} className="p-1 rounded hover:bg-blue-50 text-blue-600 mr-1" data-testid={`cat-edit-${it._id}`}>
                      <Edit3 size={13} />
                    </button>
                    <button onClick={() => handleDelete(it._id)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`cat-delete-${it._id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500 flex items-center gap-2">
        <FileSpreadsheet size={12} /> {filtered.length} ítem{filtered.length !== 1 ? "s" : ""} mostrado{filtered.length !== 1 ? "s" : ""} de {items.length} total{items.length !== 1 ? "es" : ""}.
      </p>

      <CatalogoEditModal
        open={!!editing}
        item={editing === "new" ? null : editing}
        categoria={categoria}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchAll(); }}
      />
    </div>
  );
}

// ─── Edit Modal ────────────────────────────────────────────────────────────
function CatalogoEditModal({ open, item, categoria, onClose, onSaved }) {
  const { catApi } = useCatalogoPresupuesto(); // Fix CC (2026-09-10)
  const campos = CAMPOS_MODAL[categoria] || [];
  const [form, setForm] = useState({ precio_unitario: 0, unidad: "un" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const init = { precio_unitario: 0, unidad: "" };
      campos.forEach(c => { init[c.key] = ""; });
      if (item) {
        campos.forEach(c => {
          const val = c.meta ? item.meta?.[c.key] : item[c.key];
          init[c.key] = val ?? "";
        });
        init.precio_unitario = item.precio_unitario || 0;
        init.unidad = item.unidad || "";
      }
      setForm(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, categoria]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.descripcion?.trim() && !form.modelo?.trim()) {
      toast.error("Descripción o modelo son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const meta = {};
      campos.forEach(c => { if (c.meta) meta[c.key] = c.type === "number" ? Number(form[c.key]) || 0 : (form[c.key] || "").toString().trim(); });
      const payload = {
        marca:           (form.marca       || "").toString().trim(),
        modelo:          (form.modelo      || "").toString().trim(),
        descripcion:     (form.descripcion || "").toString().trim(),
        precio_unitario: Number(form.precio_unitario) || 0,
        unidad:          (form.unidad      || "").toString().trim(),
        meta,
      };
      if (item?._id) {
        await axios.put(`${catApi}/catalogos/${categoria}/${item._id}`, payload, opts);
      } else {
        await axios.post(`${catApi}/catalogos/${categoria}`, payload, opts);
      }
      toast.success(item?._id ? "Ítem actualizado" : "Ítem creado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid={`cat-edit-modal-${categoria}`}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900 capitalize">{item ? "Editar" : "Nuevo"} — {categoria.replace("_", " ")}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-3">
          {campos.map(c => (
            <div key={c.key}>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">{c.label}</label>
              <input type={c.type || "text"} value={form[c.key] ?? ""} onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))}
                placeholder={c.placeholder}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid={`cat-input-${c.key}`} />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Precio Unitario (Gs.)</label>
              <input type="number" min="0" value={form.precio_unitario} onChange={e => setForm(f => ({ ...f, precio_unitario: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="cat-input-precio" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Unidad</label>
              <input value={form.unidad} onChange={e => setForm(f => ({ ...f, unidad: e.target.value }))}
                placeholder="m / un / rollo"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="cat-input-unidad" />
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white border-t border-gray-100 pt-4">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="cat-save-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            {saving ? "Guardando..." : (item ? "Guardar" : "Crear")}
          </button>
        </div>
      </div>
    </div>
  );
}
